import React, { useState } from "react";
import validator from "validator";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { BASE_URL } from "../constants";
import { useSetRecoilState } from "recoil";
import { authState } from "../recoil/authAtom";
import { normalizeAuthIdentity } from "../utils/auth";
import { setCsrfToken } from "../utils/api";

const OrgLogin = () => {
  const location = useLocation();
  const [loginData, setLoginData] = useState({
    orgEmail: location.state?.orgEmail || "",
    orgPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const setAuth = useSetRecoilState(authState);
  const validateForm = () => {
    const newErrors = {};

    // Validate orgEmail
    if (!loginData.orgEmail || !validator.isEmail(loginData.orgEmail)) {
      newErrors.orgEmail = "A valid email is required.";
    }

    // Validate orgPassword
    if (loginData.orgPassword.length < 8) {
      newErrors.orgPassword = "Password must be at least 8 characters long.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}api/org/login`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginData),
        });

        const data = await response.json();

        if (data.success) {
          setCsrfToken(data.csrfToken);
          console.log("Login successful", data);
          localStorage.setItem("orgId", data.org._id);
          localStorage.setItem("orgEmail", data.org.orgEmail);
          localStorage.setItem("orgID", data.org.orgID);
          localStorage.setItem("orgName", data.org.orgName);
          localStorage.setItem("orgOwnerName", data.org.orgOwnerName);
          localStorage.setItem("orgOwnerContact", data.org.orgOwnerContact);
          localStorage.setItem("orgAddress", data.org.orgAddress);
          localStorage.setItem("orgPhone", data.org.orgPhone);
          setAuth(normalizeAuthIdentity(data.org, "org"));
          alert(data.message);
          navigate("/org-dashboard");
          console.log("Asas");
          // Redirect or perform other actions on successful login
        } else {
          console.log("Error:", data.message);
          if (
            data.message &&
            (data.message.toLowerCase().includes("verify") ||
              data.message.toLowerCase().includes("verification") ||
              data.message.toLowerCase().includes("otp"))
          ) {
            const goToVerify = window.confirm(
              `${data.message}\n\nWould you like to verify your organization email now?`,
            );
            if (goToVerify) {
              navigate("/org-verify-otp", {
                state: { orgEmail: loginData.orgEmail },
              });
              return;
            }
          } else {
            alert("Error: " + data.message);
          }
        }
      } catch (error) {
        console.error("Error:", error);
        alert("Something went wrong! Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Paper elevation={3} sx={{ padding: 4, maxWidth: 400, width: "100%" }}>
        <Typography variant="h4" align="center" gutterBottom>
          Organization Login
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            variant="outlined"
            name="orgEmail"
            value={loginData.orgEmail}
            onChange={handleChange}
            error={!!errors.orgEmail}
            helperText={errors.orgEmail}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            variant="outlined"
            type="password"
            name="orgPassword"
            value={loginData.orgPassword}
            onChange={handleChange}
            error={!!errors.orgPassword}
            helperText={errors.orgPassword}
          />
          <Box sx={{ mt: 2, position: "relative" }}>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ fontWeight: "bold", padding: "10px 0" }}
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  color: "primary.main",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Need to verify organization email?{" "}
              <Link
                to="/org-verify-otp"
                state={{ orgEmail: loginData.orgEmail }}
                style={{
                  color: "#1976d2",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Verify with OTP
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Don&apos;t have an organization account?{" "}
              <Link
                to="/org-register"
                style={{
                  color: "#1976d2",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Register
              </Link>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default OrgLogin;
